// Shared handle to the active TradingView widget so the full-height tool rail
// (rendered outside the chart iframe) can drive the chart's drawing tools.
//
// Which object owns what, per charting_library.d.ts:
//   IChartingLibraryWidget (the widget, from `new TradingView.widget`)
//     line 11747+ — selectLineTool, selectedLineTool, magnetEnabled,
//     hideAllDrawingTools, lockAllDrawingTools, takeScreenshot,
//     subscribe/unsubscribe
//   IChartWidgetApi (activeChart())
//     lines 10732-11746 — removeAllShapes, resolution, setResolution,
//     onIntervalChanged, executeActionById
// Calling selectLineTool on activeChart() is a silent no-op: it is not there.

let widget = null;
const listeners = new Set();

const DEBUG = process.env.NODE_ENV !== "production";

function log(...args) {
  if (DEBUG) console.warn("[chart-tools]", ...args);
}

export function setChartWidget(w) {
  widget = w;
  if (w) log("widget registered.", describeCapabilities());
  listeners.forEach((fn) => {
    try { fn(w); } catch {}
  });
}

/** Subscribers are called immediately with the current widget, if there is one,
 *  so a rail that mounts after the chart is ready still becomes usable. */
export function subscribeChartWidget(fn) {
  listeners.add(fn);
  if (widget) {
    try { fn(widget); } catch {}
  }
  return () => listeners.delete(fn);
}

/** Reports which pieces of the drawing API are actually present. */
export function describeCapabilities() {
  return {
    widget: !!widget,
    selectLineTool: typeof widget?.selectLineTool,
    selectedLineTool: typeof widget?.selectedLineTool,
    subscribe: typeof widget?.subscribe,
    activeChart: typeof widget?.activeChart,
    removeAllShapes: typeof activeChart()?.removeAllShapes,
    magnetEnabled: typeof widget?.magnetEnabled,
  };
}

export function isChartReady() {
  return typeof widget?.selectLineTool === "function";
}

function activeChart() {
  try {
    return widget?.activeChart?.() ?? null;
  } catch {
    return null;
  }
}

/** selectLineTool is async — await it before reading the selection back.
 *  `options` carries the payload the emoji and icon tools need, e.g.
 *  selectTool("emoji", { emoji: "🚀" }); without it the tool falls back to the
 *  single default glyph from linetoolemoji.emoji. */
export async function selectTool(tool, options) {
  const w = widget;
  if (typeof w?.selectLineTool !== "function") {
    log(`selectLineTool unavailable for "${tool}".`, describeCapabilities());
    return false;
  }
  try {
    if (options) await w.selectLineTool(tool, options);
    else await w.selectLineTool(tool);
  } catch (err) {
    log(`selectLineTool("${tool}") threw:`, err);
    return false;
  }
  try {
    if (typeof w.selectedLineTool === "function") {
      const now = w.selectedLineTool();
      if (now !== tool) {
        log(`selectLineTool("${tool}") did not stick; chart reports "${now}".`);
        // The tool may still be armed, so treat this as success for the UI.
      }
      return true;
    }
  } catch {}
  return true;
}

export function currentTool() {
  try {
    return widget?.selectedLineTool?.() ?? null;
  } catch {
    return null;
  }
}

/** Fires whenever the active tool changes (e.g. reverts after a drawing ends). */
export function subscribeToolChange(fn) {
  const w = widget;
  if (typeof w?.subscribe !== "function") return () => {};
  try {
    w.subscribe("onSelectedLineToolChanged", fn);
  } catch {
    return () => {};
  }
  return () => {
    try { w.unsubscribe("onSelectedLineToolChanged", fn); } catch {}
  };
}

export function removeAllDrawings() {
  try {
    activeChart()?.removeAllShapes?.();
  } catch (err) {
    log("removeAllShapes failed:", err);
  }
}

// ── Interval + header actions ────────────────────────────────────────────
// The native chart header is disabled, so the app's top bar owns these.

export function currentResolution() {
  try {
    return activeChart()?.resolution?.() ?? null;
  } catch {
    return null;
  }
}

export async function setResolution(resolution) {
  const chart = activeChart();
  if (typeof chart?.setResolution !== "function") {
    log(`setResolution unavailable for "${resolution}".`);
    return false;
  }
  try {
    await chart.setResolution(resolution);
    return true;
  } catch (err) {
    log(`setResolution("${resolution}") failed:`, err);
    return false;
  }
}

export function subscribeResolutionChange(fn) {
  const sub = (() => {
    try {
      return activeChart()?.onIntervalChanged?.() ?? null;
    } catch {
      return null;
    }
  })();
  if (!sub?.subscribe) return () => {};
  const handler = (interval) => fn(interval);
  try {
    sub.subscribe(null, handler);
  } catch {
    return () => {};
  }
  return () => {
    try { sub.unsubscribe(null, handler); } catch {}
  };
}

/** Opens the library's own dialogs, e.g. "insertIndicator", "chartProperties". */
export function executeAction(actionId) {
  try {
    activeChart()?.executeActionById?.(actionId);
  } catch (err) {
    log(`executeActionById("${actionId}") failed:`, err);
  }
}

export function takeScreenshot() {
  try {
    widget?.takeScreenshot?.();
  } catch (err) {
    log("takeScreenshot failed:", err);
  }
}

// Widget-level toggles backed by IWatchedValue (read / write / subscribe).
const TOGGLES = {
  magnet: "magnetEnabled",
  hide: "hideAllDrawingTools",
  lock: "lockAllDrawingTools",
};

function watched(key) {
  const name = TOGGLES[key];
  if (!name) return null;
  try {
    return widget?.[name]?.() ?? null;
  } catch {
    return null;
  }
}

export function getToggle(key) {
  try { return !!watched(key)?.value(); } catch { return false; }
}

export function toggle(key) {
  const wv = watched(key);
  if (!wv) {
    log(`toggle "${key}" unavailable.`);
    return false;
  }
  try {
    const next = !wv.value();
    wv.setValue(next);
    return next;
  } catch (err) {
    log(`toggle "${key}" failed:`, err);
    return false;
  }
}
